import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { getArchiveImpactV2, type ArchiveImpactDto } from '@/lib/api/v2/projects'

interface DeleteProjectDialogProps {
  open: boolean
  projectId: string
  isSubmitting: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * SOLY-ORDER-002 fiche 17 — « Supprimer le projet » confirmation, with informed consent.
 *
 * The deletion CASCADES the sale teardown (AC-2): order, upcoming RDVs and still-cancellable
 * supplier lines go down with the project, in one transaction. In v2 archiving cancelled nothing,
 * so this dialog warned that the sale would keep living invisibly and pushed the user towards
 * « Annuler la vente » instead — there is nothing left to avert now, so it ANNOUNCES what goes
 * rather than warning against it.
 *
 * It also says the deletion is restorable, and that restoring brings the dossier back WITHOUT the
 * sale (AC-4) — a silent half-restore is what a user discovers three weeks later. It stops there:
 * the copy used to add « cela se fait séparément », pointing at a separate revival gesture that no
 * longer exists (fiches 18/19). Promising nothing is the rule here (decision Thierry 2026-08-13).
 *
 * No hard block — the human decides. Static fallback when the dry-run fails.
 */
export function DeleteProjectDialog({ open, ...props }: DeleteProjectDialogProps) {
  // Content mounts per open → fresh impact state every time, no reset effect.
  if (!open) return null
  return <DeleteProjectDialogContent {...props} />
}

function DeleteProjectDialogContent({ projectId, isSubmitting, onConfirm, onCancel }: Omit<DeleteProjectDialogProps, 'open'>) {
  const [impact, setImpact] = useState<ArchiveImpactDto | null>(null)
  const [impactFailed, setImpactFailed] = useState(false)

  useEffect(() => {
    let disposed = false
    getArchiveImpactV2(projectId)
      .then((dto) => {
        if (!disposed) setImpact(dto)
      })
      .catch(() => {
        // Fallback: the dialog stays usable without the recap, with a static caveat instead.
        if (!disposed) setImpactFailed(true)
      })
    return () => {
      disposed = true
    }
  }, [projectId])

  const confirmedCount = impact?.upcomingEvents.filter((e) => e.clientConfirmed).length ?? 0
  const upcomingCount = impact?.upcomingEvents.length ?? 0
  const showWarning = impact !== null && (impact.hasActiveSale || upcomingCount > 0)
  // Informed consent: don't let the user confirm while the dry-run is still pending — a fast
  // click would archive without ever seeing the active-sale / upcoming-RDV warning.
  const impactPending = impact === null && !impactFailed

  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="project-archive-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
          {/* Say « restaurable » plainly (AC-6): the word « Supprimer » would otherwise promise
              something irreversible that this gesture is not. */}
          <AlertDialogDescription>
            Le projet sort de la vue active et de vos statistiques. Vous pourrez le restaurer depuis la
            corbeille.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {impactFailed && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            data-testid="project-archive-impact-fallback"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Impossible de vérifier l'impact. La vente et les RDV liés seront supprimés avec le projet.
            </p>
          </div>
        )}

        {/* Fiche 17 AC-2/AC-6 — the cascade turned this from a WARNING into an ANNOUNCEMENT.
            Deleting the project used to leave the sale live but invisible, so the old copy pushed
            the user towards « Annuler la vente » instead. It now deletes the sale too, so the
            honest job here is to state what goes down with it — no amber, nothing to avert. */}
        {showWarning && (
          <div
            className="rounded-md border bg-muted/40 p-3 text-sm"
            data-testid="project-delete-cascade-notice"
          >
            <div className="space-y-1">
              <p className="font-medium">Seront supprimés avec le projet :</p>
              {impact.hasActiveSale && <p>• la vente en cours ;</p>}
              {upcomingCount > 0 && (
                <p>
                  • {upcomingCount === 1 ? '1 RDV à venir' : `${upcomingCount} RDV à venir`}
                  {confirmedCount > 0 && (
                    <span className="font-medium">
                      {' '}
                      dont {confirmedCount === 1 ? '1 confirmé par le client' : `${confirmedCount} confirmés par le client`}
                    </span>
                  )}
                  {' '}ainsi que les commandes fournisseurs encore annulables.
                </p>
              )}
              <p className="text-muted-foreground">
                Restaurer le projet le rend à nouveau visible, mais ne rétablit pas la vente.
              </p>
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isSubmitting} data-testid="project-archive-back-btn">
            Retour
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              if (!impactPending) onConfirm()
            }}
            disabled={isSubmitting || impactPending}
            data-testid="project-archive-confirm-btn"
          >
            {isSubmitting ? 'Suppression…' : 'Supprimer le projet'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
